import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { EASE } from "./Reveal";

const defaultLinks = [
  { href: "/#work", label: "Work" },
  { href: "/#process", label: "Process" },
  { href: "/#packages", label: "Packages" },
  { href: "/#faq", label: "FAQ" },
];

/**
 * Shared navigation for every DataPulse Web page. Condenses on scroll and opens a
 * full-screen menu on mobile.
 *
 * @param {Array<{href,label}>} links  Nav destinations.
 * @param {string} ctaHref             Where the primary button goes.
 * @param {string} ctaLabel            Primary button text.
 */
export function WebNav({ links = defaultLinks, ctaHref = "/#start", ctaLabel = "Get started" }) {
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        <div
          className={`transition-all duration-500 ${
            scrolled
              ? "border-b border-[#121E33] bg-[#060B14]/85 backdrop-blur-xl"
              : "border-b border-transparent"
          }`}
        >
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
            <a href="/" className="dp-focus flex items-center gap-2.5">
              <img
                src="/logo_head_clean_transparent_small.png"
                alt=""
                className="h-7 w-7"
                aria-hidden="true"
              />
              <span className="dp-display text-[17px] font-semibold tracking-[-0.02em] text-white">
                DataPulse Web
              </span>
            </a>

            <nav className="hidden items-center gap-8 md:flex" aria-label="Sections">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="dp-focus text-[14px] text-[#8FA3C0] transition-colors duration-300 hover:text-white"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <a
                href={ctaHref}
                className="dp-focus hidden rounded-full bg-[#FF6A3D] px-5 py-2 text-[13.5px] font-semibold text-white transition-colors duration-300 hover:bg-[#FF8055] sm:inline-flex"
              >
                {ctaLabel}
              </a>
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="dp-focus inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#1A2A44] text-[#B6C6DE] md:hidden"
                aria-label="Open menu"
              >
                <Menu className="h-[18px] w-[18px]" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-[#060B14]/97 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center justify-between px-5 py-3.5">
              <span className="dp-display text-[17px] font-semibold tracking-[-0.02em] text-white">
                DataPulse Web
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="dp-focus inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#1A2A44] text-[#B6C6DE]"
                aria-label="Close menu"
              >
                <X className="h-[18px] w-[18px]" aria-hidden="true" />
              </button>
            </div>
            <nav className="px-5 pt-8" aria-label="Sections">
              {links.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="dp-focus dp-display flex items-baseline justify-between border-b border-[#121E33] py-5 text-3xl font-semibold tracking-[-0.03em] text-[#EDF1F7]"
                  initial={reduce ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: EASE, delay: 0.05 + i * 0.05 }}
                >
                  {link.label}
                  <span className="dp-mono text-[11px] tracking-[0.18em] text-[#3A4E6E]">
                    0{i + 1}
                  </span>
                </motion.a>
              ))}
              <a
                href={ctaHref}
                onClick={() => setMenuOpen(false)}
                className="dp-focus mt-8 flex items-center justify-center gap-2 rounded-full bg-[#FF6A3D] py-4 text-[15px] font-semibold text-white"
              >
                {ctaLabel} <ArrowRight className="h-4 w-4" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default WebNav;
